import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, ButtonGroup, Col, Dropdown, FormLabel, Row } from 'react-bootstrap';
import FilterAccordion from "../../../Common/Filter/FilterAccordion";
import BranchSelect from "../../../Custom/Main/Branch/BranchSelect";
import InputDate from "../../../Forms/Element/InputDate";
import { errorAlert, successAlert, warnAlert } from "../../../alert/defaultAlert";
import { SearchOperation } from "../../../../../utils/ApiUtils";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import GridCustomBase from "../../../GridCustom/GridCustomBase";
import { formatDateToYYYYMMDD, formatUTCDate, getFirstDayOfCurrentMonthDate } from "../../../../../utils/DateUtils";
import Common from "../../../../../constants/Common";
import CashFlow, { getDueSoonLimitDate } from "../../../../../constants/CashFlow";
import InputText from "../../../Forms/Element/InputText";
import { useLocation, useNavigate } from "react-router-dom";
import MenuConfig from "../../../../../constants/MenuConfig";
import { useBranch } from "../../../../../context/BranchContext";
import { convertDoubleToStringPTBR } from "../../../../../utils/ConverterUtils";
import CashStatusSelect from "./PayableReceivableCashFlowTypeSelect";
import ModalPageEvent from "../../../Common/Modal/ModalPageEvent";
import ModalPage from "../../../Common/Modal/ModalPage";
import EditIcon from "../../../Icon/EditIcon";
import TrashIcon from "../../../Icon/TrashIcon";
import SaveIcon from "../../../Icon/SaveIcon";
import DetailIcon from "../../../Icon/DetailIcon";
import PayableReceivableCashFlowType from "./PayableReceivableCashFlowType";
import { ApiError } from "../../../Common/ApiError";
import { formatDreGroup } from "../Category/DreGroupFormat";
import "./PayableReceivableHistory.css";

// O vencimento chega do backend como texto ISO (YYYY-MM-DD). Comparar texto com texto
// deixa o cálculo livre de fuso: converter para Date puxaria o dia para trás.
const isoDay = (value) => (value ? String(value).slice(0, 10) : null);

// Conta em aberto é a que ainda não teve baixa. A grade se guia por paymentDate e o
// backend mantém a flag paid; exigir os dois cobre registros antigos sem a flag.
const isOpenAccount = (row) => !row.paid && !row.paymentDate;

// Cartões que representam um tipo de conta. O clique filtra a grade por ele sem tocar no
// campo Status do filtro, que alimenta o cálculo de todos os cartões.
const CARD_TYPES = {
    payable: PayableReceivableCashFlowType.PAYABLE,
    paid: PayableReceivableCashFlowType.PAYABLE,
    receivable: PayableReceivableCashFlowType.RECEIVABLE,
    received: PayableReceivableCashFlowType.RECEIVABLE
};

// Cartões cujo recorte é a carteira em aberto, não o período filtrado.
const IGNORES_PERIOD = ['due_soon', 'overdue'];

// Soma e contagem de um conjunto de contas, no formato que os cartões exibem.
const summarize = (rows) => ({
    count: rows.length,
    total: rows.reduce((sum, row) => sum + Number(row.value || 0), 0)
});

const PayableReceivableHistory = () => {

    const { t, i18n } = useTranslation();
    const { selectedBranch } = useBranch();
    const location = useLocation();
    const navigate = useNavigate();

    const alertFilters = new URLSearchParams(location.search);
    const alertFrom = alertFilters.get('from');
    const alertTo = alertFilters.get('to');
    const parseQueryDate = (value) => {
        if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
    };
    const alertStartDate = parseQueryDate(alertFilters.get('from'));
    const alertEndDate = parseQueryDate(alertFilters.get('to'));
    const isPayableAlert = alertFilters.get('alert') === 'payable';

    // Estados do filtro. Começa no primeiro dia do mês corrente para já mostrar as movimentações do mês.
    const [startDate, setStartDate] = useState(() => isPayableAlert && alertStartDate ? alertStartDate : getFirstDayOfCurrentMonthDate());
    const [endDate, setEndDate] = useState(() => isPayableAlert && alertEndDate ? alertEndDate : new Date());
    const [description, setDescription] = useState('');
    const [type, setType] = useState(() => isPayableAlert ? { value: PayableReceivableCashFlowType.PAYABLE, label: 'A pagar' } : null);
    const [branch, setBranch] = useState(null);
    const [alertFilterVersion, setAlertFilterVersion] = useState(0);

    useEffect(() => {
        if (!isPayableAlert) return;

        const start = parseQueryDate(alertFrom);
        const end = parseQueryDate(alertTo);

        // Mantém os controles visuais iguais aos filtros usados na consulta quando
        // o alerta é aberto sem desmontar a tela atual.
        if (start) setStartDate(start);
        if (end) setEndDate(end);
        setType({ value: PayableReceivableCashFlowType.PAYABLE, label: 'A pagar' });
        setPage(0);
        // Faz a busca esperar a renderização que contém os valores sincronizados.
        setAlertFilterVersion(version => version + 1);
    }, [isPayableAlert, alertFrom, alertTo]);

    // Estados da grid
    const [pageable, setPageable] = useState(null);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState([]);
    const [size, setSize] = useState(Common.DEFAULT_ROWS_PER_PAGE);
    const [selectedRows, setSelectedRows] = useState([]);
    const workspaceRef = useRef(null);
    const [showLoading, setShowLoading] = useState(false);

    // Estados de modal
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showReverseConfirmation, setShowReverseConfirmation] = useState(false);
    const [showPaymentDetail, setShowPaymentDetail] = useState(false);
    const [selectedDetailRow, setSelectedDetailRow] = useState(null);

    // Estados de estatísticas
    // Cada indicador guarda valor e quantidade, porque o cartão mostra os dois.
    const [totalPayable, setTotalPayable] = useState({ count: 0, total: 0 });
    const [totalReceivable, setTotalReceivable] = useState({ count: 0, total: 0 });
    const [totalPaid, setTotalPaid] = useState({ count: 0, total: 0 });
    const [totalReceived, setTotalReceived] = useState({ count: 0, total: 0 });
    const [dueSoon, setDueSoon] = useState({ count: 0, total: 0 });
    const [overdue, setOverdue] = useState({ count: 0, total: 0 });
    // Cartão em destaque, que é o recorte aplicado à grade. Vale só para a lista: os
    // valores dos cartões nunca mudam por causa dele.
    const [cardFilter, setCardFilter] = useState(null);

    const selectRow = (id) => {
        setSelectedRows(current => current[0] === id ? [] : [id]);
    }

    // Modais e menus continuam a ação iniciada na linha e não devem limpar a seleção.
    useEffect(() => {
        const clearSelection = (event) => {
            if (workspaceRef.current?.contains(event.target) ||
                event.target.closest?.('.modal, .swal2-container, .dropdown-menu, .offcanvas')) return;
            setSelectedRows([]);
        };
        document.addEventListener('mousedown', clearSelection);
        return () => document.removeEventListener('mousedown', clearSelection);
    }, []);

    const openPaymentDetail = (row) => {
        setSelectedDetailRow(row);
        setShowPaymentDetail(true);
    }

    // Função de busca otimizada
    const search = useCallback(() => {
        // Validações
        if (!startDate) {
            warnAlert(t('validation.required.start_date'));
            return;
        }
        if (!endDate) {
            warnAlert(t('validation.required.end_date'));
            return;
        }

        // Separado do período porque a consulta da carteira em aberto reaproveita estes
        // filtros sem as datas.
        const contextFilters = [];

        const dateFilters = [
            {
                field: "dueDate",
                value: formatDateToYYYYMMDD(startDate),
                operation: SearchOperation.GREATER_THAN_EQUAL
            },
            {
                field: "dueDate",
                value: formatDateToYYYYMMDD(endDate),
                operation: SearchOperation.LESS_THAN_EQUAL
            }
        ];

        // Filtro de filial (contexto tem prioridade sobre seleção manual)
        if (selectedBranch !== null) {
            contextFilters.push({
                field: "branch.id",
                value: selectedBranch.id,
                operation: SearchOperation.EQUAL
            });
        } else if (branch) {
            contextFilters.push({
                field: "branch.id",
                value: branch.value,
                operation: SearchOperation.EQUAL
            });
        }

        // Filtro de descrição
        if (description) {
            contextFilters.push({
                field: "description",
                value: description,
                operation: SearchOperation.MATCH
            });
        }

        // Filtro de tipo
        if (type) {
            contextFilters.push({
                field: "type",
                value: type.value,
                operation: SearchOperation.EQUAL
            });
        }

        const todayIso = formatDateToYYYYMMDD(new Date());
        const dueSoonLimitIso = formatDateToYYYYMMDD(getDueSoonLimitDate());

        // Recorte que o cartão clicado aplica. Vale só para a grade: nenhum cartão entra
        // no cálculo do outro, senão clicar em "Pago" faria "Vencidos" perder metade das
        // contas. Por isso o clique também não mexe no campo Status do filtro.
        const cardCriteria = [];
        const cardType = CARD_TYPES[cardFilter];
        if (cardType) {
            cardCriteria.push({ field: "type", value: cardType, operation: SearchOperation.EQUAL });
        }
        if (cardFilter === 'payable' || cardFilter === 'receivable') {
            // "A pagar" é o que falta pagar: a conta já baixada pertence ao cartão "Pago".
            cardCriteria.push({ field: "paid", value: false, operation: SearchOperation.EQUAL });
        } else if (cardFilter === 'paid' || cardFilter === 'received') {
            cardCriteria.push({ field: "paid", value: true, operation: SearchOperation.EQUAL });
        } else if (cardFilter === 'due_soon') {
            cardCriteria.push(
                { field: "paid", value: false, operation: SearchOperation.EQUAL },
                { field: "dueDate", value: todayIso, operation: SearchOperation.GREATER_THAN_EQUAL },
                { field: "dueDate", value: dueSoonLimitIso, operation: SearchOperation.LESS_THAN_EQUAL }
            );
        } else if (cardFilter === 'overdue') {
            cardCriteria.push(
                { field: "paid", value: false, operation: SearchOperation.EQUAL },
                { field: "dueDate", value: todayIso, operation: SearchOperation.LESS_THAN }
            );
        }

        // O tipo do cartão manda sobre o do filtro: dois valores diferentes no mesmo
        // campo se anulariam e a lista viria vazia.
        const gridContextFilters = cardType
            ? contextFilters.filter(criteria => criteria.field !== "type")
            : contextFilters;

        // Vencido e a vencer são posições em relação a hoje, e os cartões somam a carteira
        // inteira. A lista larga o período junto, senão o gestor clicaria num cartão de
        // R$ 5.000,00 e veria uma lista de R$ 800,00.
        const gridDateFilters = IGNORES_PERIOD.includes(cardFilter) ? [] : dateFilters;

        const payload = {
            filters: [...gridContextFilters, ...gridDateFilters, ...cardCriteria],
            page,
            size,
            orders: [{
                field: "id",
                direction: "DESC"
            }]
        };

        setShowLoading(true);

        // Busca para paginação
        axiosInstance.post(Api.cash_flow.payable_receivable + "/pageable", payload)
            .then((response) => {
                setPageable(response.data);
                setRows(response.data.content);
                setSelectedRows(current => current.filter(id => response.data.content.some(row => row.id === id)));
            })
            .catch(error => errorAlert(error))
            .finally(() => {
                setShowLoading(false);
            });

        // Busca separada para cálculo de estatísticas (todos os registros)
        const statsPayload = {
            filters: [...contextFilters, ...dateFilters],
            page: 0,
            size: 10000, // Busca todos os registros para cálculo correto
            orders: [{
                field: "id",
                direction: "DESC"
            }]
        };

        axiosInstance.post(Api.cash_flow.payable_receivable + "/pageable", statsPayload)
            .then((response) => {
                // Calcula estatísticas com TODOS os registros
                const data = response.data.content || [];

                // Os quatro cartões são recortes que não se sobrepõem: "A pagar" é o que
                // falta pagar e "Pago" o que já saiu, nunca a mesma conta nos dois. Somar
                // a conta baixada também em "A pagar" fazia o cartão prometer uma dívida
                // que não existe mais, e era o que a lista desmentia ao ser aberta.
                const isPayable = (row) => row.type === PayableReceivableCashFlowType.PAYABLE;
                const settled = (row) => !isOpenAccount(row);

                setTotalPayable(summarize(data.filter(row => isPayable(row) && isOpenAccount(row))));
                setTotalPaid(summarize(data.filter(row => isPayable(row) && settled(row))));
                setTotalReceivable(summarize(data.filter(row => !isPayable(row) && isOpenAccount(row))));
                setTotalReceived(summarize(data.filter(row => !isPayable(row) && settled(row))));
            })
            .catch(error => console.error('Erro ao calcular estatísticas:', error));

        // Terceira busca: a carteira em aberto, sem recorte de data. Vencido e a vencer
        // são posições em relação a hoje, não ao período: o atraso antigo continua sendo
        // dívida quando o gestor olha só o mês corrente, e a janela de sete dias é futura,
        // fora de um período que por padrão termina hoje.
        const openPayload = {
            filters: [
                ...contextFilters,
                { field: "paid", value: false, operation: SearchOperation.EQUAL }
            ],
            page: 0,
            size: 10000,
            orders: [{
                field: "dueDate",
                direction: "ASC"
            }]
        };

        axiosInstance.post(Api.cash_flow.payable_receivable + "/pageable", openPayload)
            .then((response) => {
                // Vencido e a vencer valem para os dois tipos: atraso em conta a pagar é
                // dívida, em conta a receber é inadimplência, e os dois pedem ação.
                const open = (response.data.content || []).filter(isOpenAccount);

                setDueSoon(summarize(open.filter(row => {
                    const due = isoDay(row.dueDate);
                    return due >= todayIso && due <= dueSoonLimitIso;
                })));

                setOverdue(summarize(open.filter(row => isoDay(row.dueDate) < todayIso)));
            })
            .catch(error => console.error('Erro ao buscar contas em aberto:', error));
    }, [startDate, endDate, description, branch, type, selectedBranch, page, size, cardFilter, t]);

    // Navega para o formulário de novo
    const redirectToNew = useCallback(() => {
        navigate(MenuConfig.cash_flow.payable_receivable.route + "/new");
    }, [navigate]);

    // Navega para o formulário de edição
    const redirectToUpdate = useCallback(() => {
        if (!checkRows()) {
            return;
        }

        const selected = rows.find(row => row.id === selectedRows[0]);
        if (selected && selected.paymentDate !== null) {
            setShowReverseConfirmation(true);
            return;
        }

        navigate(MenuConfig.cash_flow.payable_receivable.route + "/new/" + selectedRows[0]);
    }, [selectedRows, rows, navigate, t]);

    const onReverseConfirmation = useCallback(() => {
        const id = selectedRows[0];
        axiosInstance.post(`${Api.cash_flow.payable_receivable}/${id}/reverse-payment`)
            .then(() => navigate(MenuConfig.cash_flow.payable_receivable.route + "/new/" + id))
            .catch(error => ApiError(error))
            .finally(() => setShowReverseConfirmation(false));
    }, [selectedRows, navigate]);

    // Exibe modal de confirmação de exclusão
    const onDelete = useCallback(() => {
        if (!checkRows()) {
            return;
        }
        setShowDeleteConfirmation(true);
    }, [selectedRows, t]);

    // Confirma a exclusão
    const onDeleteConfirmation = useCallback(() => {
        axiosInstance.delete(`${Api.cash_flow.payable_receivable}/${selectedRows[0]}`)
            .then(() => {
                successAlert(t('message.transaction_successfully'), () => {
                    search();
                });
            })
            .catch(error => ApiError(error))
            .finally(() => setShowDeleteConfirmation(false));
    }, [selectedRows, search, t]);

    // Valida seleção de linhas
    const checkRows = useCallback(() => {
        if (selectedRows.length === 0) {
            warnAlert(t('validation.required.row'));
            return false;
        }
        if (selectedRows.length > 1) {
            warnAlert(t('validation.required.row_select_one'));
            return false;
        }
        return true;
    }, [selectedRows, t]);

    // Handler para o formulário de filtros
    const handleSearchSubmit = useCallback((e) => {
        e?.preventDefault();
        setPage(0);
        search();
    }, [search]);

    // Handler para atalhos de data
    const handleQuickDateSelect = useCallback((startDate, endDate) => {
        setStartDate(startDate);
        setEndDate(endDate);
    }, []);

    // Handler para limpar filtros
    const handleClearFilters = useCallback(() => {
        setStartDate(getFirstDayOfCurrentMonthDate());
        setEndDate(new Date());
        setDescription('');
        setType(null);
        setCardFilter(null);
        if (selectedBranch === null) {
            setBranch(null);
        }
    }, [selectedBranch]);

    // Busca automática ao mudar página, tamanho ou filial do contexto
    useEffect(() => {
        search();
    }, [page, size, selectedBranch, startDate, endDate, type, cardFilter, alertFilterVersion]);

    // Sincroniza o campo branch com o contexto selectedBranch
    useEffect(() => {
        if (selectedBranch !== null) {
            setBranch({ value: selectedBranch.id, label: selectedBranch.name });
        } else {
            setBranch(null);
        }
    }, [selectedBranch]);

    // Clicar no cartão em destaque desfaz o recorte, que é como o gestor volta à lista
    // cheia sem procurar o botão de limpar.
    const handleCardClick = useCallback((card) => {
        setPage(0);
        setCardFilter(current => (current === card ? null : card));
    }, []);

    const periodLabel = `${startDate.toLocaleDateString(i18n.language)} - ${endDate.toLocaleDateString(i18n.language)}`;
    // Saldo do que efetivamente passou pelo caixa no período.
    const balance = totalReceived.total - totalPaid.total;

    // Um cartão por recorte da mesma consulta. O campo "card" é o que ele filtra na lista;
    // sem ele o cartão é apenas leitura. Os rótulos dizem a situação da conta, e a linha de
    // apoio repete em palavras o que o cartão contou, porque "A pagar" e "Pago" sozinhos
    // não deixam claro que um é o que falta e o outro o que já saiu.
    const metricCards = [
        {
            key: 'payable', card: 'payable', variant: 'danger', icon: 'fas fa-minus-circle',
            label: t('cash_flow.payable_open'), value: totalPayable.total, valueClass: 'text-danger',
            hint: t('cash_flow.open_accounts_count', { count: totalPayable.count })
        },
        {
            key: 'receivable', card: 'receivable', variant: 'success', icon: 'fas fa-plus-circle',
            label: t('cash_flow.receivable_open'), value: totalReceivable.total, valueClass: 'text-success',
            hint: t('cash_flow.open_accounts_count', { count: totalReceivable.count })
        },
        {
            key: 'paid', card: 'paid', variant: 'warning', icon: 'fas fa-hand-holding-usd',
            label: t('cash_flow.paid'), value: totalPaid.total, valueClass: 'text-warning',
            hint: t('cash_flow.settled_accounts_count', { count: totalPaid.count })
        },
        {
            key: 'received', card: 'received', variant: 'info', icon: 'fas fa-wallet',
            label: t('cash_flow.received'), value: totalReceived.total, valueClass: 'text-info',
            hint: t('cash_flow.settled_accounts_count', { count: totalReceived.count })
        },
        {
            key: 'due_soon', card: 'due_soon', variant: 'attention', icon: 'fas fa-clock',
            label: t('cash_flow.due_soon', { days: CashFlow.DUE_SOON_DAYS }), value: dueSoon.total, valueClass: 'payable-history__metric-value--attention',
            hint: t('cash_flow.open_accounts_any_date_count', { count: dueSoon.count })
        },
        {
            key: 'overdue', card: 'overdue', variant: 'overdue', icon: 'fas fa-triangle-exclamation',
            label: t('cash_flow.overdue'), value: overdue.total, valueClass: 'text-danger',
            hint: t('cash_flow.open_accounts_any_date_count', { count: overdue.count })
        },
        {
            key: 'balance', card: null, variant: 'primary', icon: 'fas fa-balance-scale',
            label: t('balance'), value: balance, valueClass: balance >= 0 ? 'text-primary' : 'text-danger',
            hint: t('cash_flow.balance_settled_hint')
        }
    ];
    const formatPaymentMethod = (paymentMethod) => paymentMethod ? t(paymentMethod.toLowerCase()) : '-';
    const installmentLabel = (row) => row.recurrenceNumber && row.recurrenceTotal
        ? `${row.recurrenceNumber}/${row.recurrenceTotal}`
        : null;

    return (
        <div ref={workspaceRef}>
            <ModalPageEvent
                title={t('remove')}
                show={showDeleteConfirmation}
                setShow={setShowDeleteConfirmation}
                child={
                    <>
                        <p>
                            {
                                t('message.remove_the_selected_item')
                            }
                        </p>
                    </>
                }
                size="md"
                centered={true}
                btnText={t('yes')}
                btnOnClick={() => onDeleteConfirmation()}
                btnCloseOnClick={() => setShowDeleteConfirmation(false)}
                btnCloseText={t('no')}
            />

            <ModalPageEvent
                title="Estornar pagamento e editar"
                show={showReverseConfirmation}
                setShow={setShowReverseConfirmation}
                child={
                    <p>
                        Este lançamento já está pago/recebido. Se continuar, o pagamento será estornado,
                        a movimentação correspondente será removida do caixa e o lançamento voltará para
                        <strong> em aberto</strong>. Depois da correção, será necessário baixá-lo novamente
                        para registrar uma nova movimentação. Deseja continuar?
                    </p>
                }
                size="md"
                centered={true}
                btnText={t('yes')}
                btnOnClick={onReverseConfirmation}
                btnCloseOnClick={() => setShowReverseConfirmation(false)}
                btnCloseText={t('no')}
            />

            <ModalPage
                title={t('details')}
                show={showPaymentDetail}
                setShow={setShowPaymentDetail}
                size="lg"
                centered={true}
                child={
                    selectedDetailRow && (
                        <div className="payable-history__detail">
                            <div className={`payable-history__detail-hero ${selectedDetailRow.type === PayableReceivableCashFlowType.PAYABLE ? 'payable-history__detail-hero--payable' : 'payable-history__detail-hero--receivable'}`}>
                                <div>
                                    <span className="payable-history__detail-kicker">
                                        {selectedDetailRow.type === PayableReceivableCashFlowType.PAYABLE ? t('cash_flow.payable') : t('cash_flow.receivable')}
                                    </span>
                                    <h5 className="payable-history__detail-name mb-1">{selectedDetailRow.description}</h5>
                                    {installmentLabel(selectedDetailRow) && (
                                        <Badge bg="secondary" className="me-2">
                                            <i className="fas fa-layer-group me-1"></i>
                                            {t('cash_flow.installment')} {installmentLabel(selectedDetailRow)}
                                        </Badge>
                                    )}
                                    <span className="text-muted">{selectedDetailRow.branch?.name || '-'}</span>
                                </div>
                                <strong className={selectedDetailRow.type === PayableReceivableCashFlowType.PAYABLE ? 'text-danger' : 'text-success'}>
                                    {t('currency_symbol')} {convertDoubleToStringPTBR(selectedDetailRow.value)}
                                </strong>
                            </div>

                            <div className="payable-history__detail-grid">
                                <div className="payable-history__detail-item">
                                    <span>{t('due_date')}</span>
                                    <strong>{formatUTCDate(selectedDetailRow.dueDate)}</strong>
                                </div>
                                <div className="payable-history__detail-item">
                                    <span>{t('cash_flow.payment_received_date')}</span>
                                    <strong>{selectedDetailRow.paymentDate ? formatUTCDate(selectedDetailRow.paymentDate) : '-'}</strong>
                                </div>
                                <div className="payable-history__detail-item">
                                    <span>{t('payment_method')}</span>
                                    <strong>{formatPaymentMethod(selectedDetailRow.paymentMethod)}</strong>
                                </div>
                                <div className="payable-history__detail-item">
                                    <span>{t('status')}</span>
                                    <strong className={selectedDetailRow.paymentDate ? 'text-success' : 'text-warning'}>
                                        {selectedDetailRow.paymentDate
                                            ? (selectedDetailRow.type === PayableReceivableCashFlowType.PAYABLE ? t('cash_flow.paid') : t('cash_flow.received'))
                                            : t('pending')}
                                    </strong>
                                </div>
                                <div className="payable-history__detail-item">
                                    <span>{t('category')}</span>
                                    <strong>{selectedDetailRow.category?.name || '-'}</strong>
                                </div>
                                <div className="payable-history__detail-item">
                                    <span>{t('cash_flow.dre_group')}</span>
                                        <strong>{formatDreGroup(selectedDetailRow.category?.dreGroup) || '-'}</strong>
                                    </div>
                                    {installmentLabel(selectedDetailRow) && (
                                        <div className="payable-history__detail-item">
                                            <span>{t('cash_flow.installment')}</span>
                                            <strong>{installmentLabel(selectedDetailRow)}</strong>
                                        </div>
                                    )}
                                </div>
                        </div>
                    )
                }
            />

            <div className="payable-history__filter-shell mb-4">
                <FilterAccordion
                    onSearch={handleSearchSubmit}
                    onClear={handleClearFilters}
                    showQuickDates={true}
                    onQuickDateSelect={handleQuickDateSelect}
                >
                    <Row className="mb-3">
                        <Col md={12}>
                            <small className="text-muted">{periodLabel}</small>
                        </Col>
                    </Row>
                    <Row className="g-3">
                        <Col lg={2} md={6}>
                            <FormLabel>{t('start_date')}</FormLabel>
                            <InputDate
                                onChange={setStartDate}
                                selected={startDate}
                            />
                        </Col>
                        <Col lg={2} md={6}>
                            <FormLabel>{t('end_date')}</FormLabel>
                            <InputDate
                                onChange={setEndDate}
                                selected={endDate}
                            />
                        </Col>
                        <Col lg={3} md={6}>
                            <FormLabel>{t('description')}</FormLabel>
                            <InputText
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </Col>
                        <Col lg={3} md={6}>
                            <FormLabel>{t('branch')}</FormLabel>
                            <BranchSelect
                                value={branch}
                                onChange={setBranch}
                                isDisabled={selectedBranch !== null}
                            />
                        </Col>
                        <Col lg={2} md={6}>
                            <FormLabel>{t('status')}</FormLabel>
                            <CashStatusSelect
                                value={type}
                                onChange={setType}
                                isClearable={true}
                            />
                        </Col>
                    </Row>
                </FilterAccordion>
            </div>

            <div className="payable-history__metrics mb-3">
                {metricCards.map(card => {
                    const active = card.card !== null && cardFilter === card.card;
                    const className = `payable-history__metric-card payable-history__metric-card--${card.variant}`
                        + (card.card ? ' payable-history__metric-card--clickable' : '')
                        + (active ? ' payable-history__metric-card--active' : '');

                    const content = (
                        <div className="payable-history__metric-head">
                            <div>
                                <span className="payable-history__metric-label">{card.label}</span>
                                <strong className={`payable-history__metric-value ${card.valueClass}`}>
                                    {t('currency_symbol')} {convertDoubleToStringPTBR(card.value)}
                                </strong>
                                {card.hint && (
                                    <span className="payable-history__metric-hint">{card.hint}</span>
                                )}
                            </div>
                            <span className="payable-history__metric-icon">
                                <i className={card.icon}></i>
                            </span>
                        </div>
                    );

                    // O saldo é leitura: não existe lista que corresponda a ele.
                    if (!card.card) {
                        return <div key={card.key} className={className}>{content}</div>;
                    }

                    return (
                        <button
                            key={card.key}
                            type="button"
                            className={className}
                            aria-pressed={active}
                            title={t('cash_flow.metric_card_filter_hint')}
                            onClick={() => handleCardClick(card.card)}
                        >
                            {content}
                        </button>
                    );
                })}
            </div>

            {IGNORES_PERIOD.includes(cardFilter) && (
                <div className="payable-history__period-notice mb-4" role="status">
                    <i className="fas fa-circle-info" aria-hidden="true"></i>
                    <span>{t('cash_flow.card_ignores_period_notice')}</span>
                </div>
            )}

            <Row className="mb-3">
                <Col md={12}>
                    <ButtonGroup>
                        <Dropdown>
                            <Dropdown.Toggle className="me-2" variant="primary">
                                {t('actions')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => redirectToNew()}>
                                    <SaveIcon /> {t('new')}
                                </Dropdown.Item>
                                {/* Sem "disabled": o clique sempre dispara e checkRows() avisa se não houver registro selecionado. */}
                                <Dropdown.Item onClick={() => redirectToUpdate()}>
                                    <EditIcon /> {t('update')}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => onDelete()}>
                                    <TrashIcon /> {t('remove')}
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </ButtonGroup>
                </Col>
            </Row>

            <GridCustomBase
                pageable={pageable}
                setPage={setPage}
                setSize={setSize}
                showLoading={showLoading}
                selectedCount={selectedRows.length}>
                <table className="table table-list i-table style-1 mb-0 border-0 payable-history__table" id="guestTable-payable-receivable" style={{ minWidth: 1240 }}>
                    <thead>
                        <tr>
                            <th>{t("description")}</th>
                            <th>{t("value")}</th>
                            <th>{t("due_date_simplified")}</th>
                            <th>{t("cash_flow.payment_received_date")}</th>
                            <th>{t("payment_method_simplified")}</th>
                            <th>{t("branch")}</th>
                            <th>{t("cash_flow.dre_group")}</th>
                            <th className="text-center">{t("type")}</th>
                            <th className="bg-none" style={{ width: 60 }} aria-label={t('actions')}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan="9" className="text-center py-5">
                                    <div className="text-muted">
                                        <i className="fas fa-file-invoice-dollar fa-2x mb-3 d-block opacity-50"></i>
                                        <strong className="d-block mb-1">{t('cash_flow.payable_receivable_empty_title')}</strong>
                                        <span>{t('cash_flow.payable_receivable_empty_hint')}</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {rows.map((row, index) => {
                            const isPaid = row.paymentDate !== null;
                            const isPayable = row.type === PayableReceivableCashFlowType.PAYABLE;
                            return (
                                <tr key={row.id || index}
                                    className={`align-middle h6 payable-history__row ${isPaid ? 'payable-history__row--settled' : ''} ${selectedRows.includes(row.id) ? 'payable-history__row--selected' : ''}`}
                                    onClick={() => selectRow(row.id)}
                                    onKeyDown={(event) => {
                                        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                                            event.preventDefault();
                                            selectRow(row.id);
                                        }
                                    }}
                                    tabIndex={0}
                                    aria-selected={selectedRows.includes(row.id)}>
                                    <td>
                                        <strong className={isPayable ? "text-danger" : "text-success"}>
                                            <i className={`fa fa-${isPayable ? 'minus' : 'plus'}-circle me-1`}></i>
                                            {row.description}
                                        </strong>
                                        {installmentLabel(row) && (
                                            <Badge bg="secondary" className="ms-2">
                                                {t('cash_flow.installment')} {installmentLabel(row)}
                                            </Badge>
                                        )}
                                    </td>
                                    <td>
                                        <strong className={isPayable ? "text-danger" : 'text-success'} style={{ fontSize: '1.1em' }}>
                                            {t('currency_symbol')} {convertDoubleToStringPTBR(row.value)}
                                        </strong>
                                    </td>
                                    <td>
                                        <i className="fa fa-calendar text-muted me-1"></i>
                                        {formatUTCDate(row.dueDate)}
                                    </td>
                                    <td>
                                        {isPaid ? (
                                            <span className="text-success fw-semibold">
                                                <i className="fa fa-check-circle me-1"></i>
                                                {formatUTCDate(row.paymentDate)}
                                            </span>
                                        ) : (
                                            <span className="text-muted">-</span>
                                        )}
                                    </td>
                                    <td>
                                        {row.paymentMethod !== null ? (
                                            <Badge bg="secondary" className="badge-lg">
                                                <i className="fa fa-credit-card me-1"></i>
                                                {t(row.paymentMethod.toLowerCase())}
                                            </Badge>
                                        ) : (
                                            <span className="text-muted">-</span>
                                        )}
                                    </td>
                                    <td>
                                        <Badge bg="info" className="badge-lg">
                                            {row.branch.name}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Badge bg={row.category?.dreGroup === 'RECEITA' ? 'success' : 'secondary'} className="badge-lg">
                                            {formatDreGroup(row.category?.dreGroup) || '-'}
                                        </Badge>
                                    </td>
                                    <td className="text-center">
                                        <Badge bg={isPayable ? 'danger' : 'success'} className="badge-lg">
                                            <i className={`fa fa-${isPayable ? 'arrow-down' : 'arrow-up'} me-1`}></i>
                                            {isPayable ? t('cash_flow.payable') : t('cash_flow.receivable')}
                                        </Badge>
                                    </td>
                                    <td className="payable-history__actions-cell" onClick={event => event.stopPropagation()}>
                                        <Dropdown className="d-inline-flex">
                                            <Dropdown.Toggle variant="link" className="payable-history__row-action i-false" aria-label={t('actions')}>
                                                <i className="fas fa-ellipsis-v" aria-hidden="true"></i>
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => openPaymentDetail(row)}>
                                                    <DetailIcon className="me-2" /> {t('details')}
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </GridCustomBase>
        </div>
    );
}

export default PayableReceivableHistory;
